import { z } from "zod";

/**
 * TARAFLAR ARASI MUTABAKAT (CLOS-161 `[154]`).
 *
 * Mutabakat sözleşmenin GERÇEK TARAFLARI arasındaki uzlaşmadır; iç onay (CLOS-158)
 * organizasyonun kendi karar zinciridir. İkisi ayrı akış, ayrı hukuki ağırlık.
 */

export const contractReconciliationConfigSchema = z.object({
  version: z.literal(1),
  /** Tek akışta en fazla kaç taraf — sınırsız liste görev seli üretir. */
  maxParties: z.number().int().min(2),
  maxDueDays: z.number().int().min(1),
  /** Mutabık olmama gerekçesi politikası (`[155]` CLOS-162 tüketecek). */
  disagreementRequiresNote: z.boolean(),
  decisionNoteMinLength: z.number().int().min(0),
});
export type ContractReconciliationConfig = z.infer<typeof contractReconciliationConfigSchema>;

const tarafSemasi = z.strictObject({
  /** Sözleşmenin gerçek tarafı (CLOS-111 kaydı). */
  partyId: z.uuid("Geçerli bir sözleşme tarafı seçilmelidir"),
  /** O taraf adına karar verecek gerçek kişi. */
  assigneeUserId: z.uuid("Geçerli bir kullanıcı seçilmelidir"),
  /**
   * Temsilcinin karar vereceği bağlam. Sahip kiracı ya da sözleşmeye katılan
   * organizasyon (CLOS-245) olabilir; sunucu ikisini de işlem anında doğruluyor.
   */
  assigneeTenantId: z.uuid("Geçerli bir çalışma alanı seçilmelidir"),
});

export const startContractReconciliationSchema = z.strictObject({
  versionId: z.uuid("Geçerli bir sözleşme versiyonu seçilmelidir"),
  /** ⚠ En az İKİ taraf: tek taraflı mutabakat diye bir şey yok. */
  parties: z.array(tarafSemasi).min(2, "Mutabakat en az iki taraf gerektirir"),
  /**
   * ⚠ Son tarih ZORUNLU (ADR-0065, Rapor 5 §5) — süreç ölçülebilir gecikme taşımak
   * zorunda; eski satırlar null kalır.
   */
  dueDate: z.iso.datetime({ message: "Geçerli bir son tarih girin" }),
  description: z.string().trim().max(2000, "Açıklama en fazla 2000 karakter olabilir").optional(),
});
export type StartContractReconciliationInput = z.infer<typeof startContractReconciliationSchema>;

export const CONTRACT_RECONCILIATION_STATUSES = [
  "open",
  "cancelled",
  "agreed",
  "disagreed",
  "changes_requested",
  /** ⚠ İPTAL DEĞİL: yeni versiyon doğduğu için sistemin vardığı sonuç (CLOS-163 `[156]`). */
  "superseded",
  /**
   * ⚠ İPTAL DE DEĞİL, GEÇERSİZLEŞME DE DEĞİL: mutabık olan tarafın kendi beyanını GERİ
   * ÇEKMESİ (Versiyon Bazlı Workflow raporu §9.2). Üçü ayrı sebeple aynı yere varıyor ve
   * ayrı kalmalı — "kim vazgeçti" sorusunun cevabı üçünde farklı.
   */
  "withdrawn",
] as const;
export type ContractReconciliationStatus = (typeof CONTRACT_RECONCILIATION_STATUSES)[number];

export const CONTRACT_RECONCILIATION_STATUS_NAMES: Record<ContractReconciliationStatus, string> = {
  open: "Mutabakat bekleniyor",
  cancelled: "İptal edildi",
  agreed: "Taraflar mutabık",
  disagreed: "Mutabakat sağlanamadı",
  changes_requested: "Değişiklik istendi",
  superseded: "Yeni versiyon nedeniyle geçersiz",
  withdrawn: "Geri çekildi",
};

export const CONTRACT_RECONCILIATION_TASK_STATUSES = [
  "pending",
  "agreed",
  "disagreed",
  /** ⚠ `changes_requested` `disagreed`'dan AYRI: "böyle olmaz" ile "şu düzeltilirse olur". */
  "changes_requested",
  "cancelled",
  /** ⚠ İPTAL DEĞİL: yeni versiyon doğduğu için düşen görev (CLOS-163 `[156]`). */
  "superseded",
  /** ⚠ Tarafın KENDİ mutabık beyanını geri çekmesi (rapor §9.2); ilk karar silinmiyor. */
  "withdrawn",
] as const;

/**
 * Taraf adına verilebilecek KARAR (CLOS-162 `[155]`). Üçü de terminal — karar geri alınmaz;
 * düzeltme gerekiyorsa akış iptal edilip yenisi açılır.
 */
export const CONTRACT_RECONCILIATION_DECISIONS = [
  "agreed",
  "disagreed",
  "changes_requested",
] as const;
export type ContractReconciliationDecision = (typeof CONTRACT_RECONCILIATION_DECISIONS)[number];

export const decideContractReconciliationTaskSchema = z.strictObject({
  decision: z.enum(CONTRACT_RECONCILIATION_DECISIONS, {
    message: "Geçerli bir karar seçilmelidir",
  }),
  note: z.string().trim().max(2000, "Gerekçe en fazla 2000 karakter olabilir").optional(),
});
export type DecideContractReconciliationTaskInput = z.infer<
  typeof decideContractReconciliationTaskSchema
>;

/**
 * MUTABAKAT BEYANINI GERİ ÇEKME (rapor §9.2).
 *
 * ⚠ Gerekçe OPSİYONEL DEĞİL — karar şemasındaki `note`'tan farkı bu. Gerekçesiz bir geri
 * çekme, karşı tarafa "neden" sorusunu bırakır ve mutabakatın bozulması sıradan bir
 * düzenleme değildir.
 */
export const withdrawContractReconciliationSchema = z.strictObject({
  reason: z
    .string({ error: "Gerekçe zorunludur" })
    .trim()
    .min(1, "Gerekçe zorunludur")
    .max(2000, "Gerekçe en fazla 2000 karakter olabilir"),
});
export type WithdrawContractReconciliationInput = z.infer<
  typeof withdrawContractReconciliationSchema
>;
export type ContractReconciliationTaskStatus =
  (typeof CONTRACT_RECONCILIATION_TASK_STATUSES)[number];

/** Kullanıcıya görünen adlar ŞEMADA (kriter 4). */
export const CONTRACT_RECONCILIATION_TASK_STATUS_NAMES: Record<
  ContractReconciliationTaskStatus,
  string
> = {
  pending: "Bekliyor",
  agreed: "Mutabık",
  disagreed: "Mutabık değil",
  changes_requested: "Değişiklik istedi",
  cancelled: "İptal edildi",
  superseded: "Yeni versiyon nedeniyle geçersiz",
  withdrawn: "Beyanını geri çekti",
};

export type ContractReconciliationTaskSummary = {
  id: string;
  partyId: string;
  partyName: string;
  assigneeUserId: string;
  assigneeName: string;
  assigneeTenantId: string;
  status: ContractReconciliationTaskStatus;
  decisionNote: string | null;
  resolvedAt: string | null;
  /**
   * ⚠ GERİ ÇEKME AYRI ALANLARDA: ilk kararın izi (`decisionNote`, `resolvedAt`) silinmiyor.
   * Rapor §9.2 ekranda ikisini birden istiyor — "İlk mutabakat 04.09 17:30 · Geri çekilme
   * 05.09 09:15 · Gerekçe: ...".
   */
  withdrawnAt: string | null;
  withdrawnByName: string | null;
  withdrawalReason: string | null;
};

export type ContractReconciliationSummary = {
  id: string;
  contractId: string;
  versionId: string;
  versionNumber: number;
  status: ContractReconciliationStatus;
  dueDate: string | null;
  description: string | null;
  startedByName: string;
  createdAt: string;
  tasks: ContractReconciliationTaskSummary[];
};
